import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import {
    FleetTrailer,
    FleetTrailerSearchParams,
    FleetTrailerType,
    FleetTrailerTypeSearchParams
} from './models/trailer.model';

@Injectable({
    providedIn: 'root'
})
export class FleetTrailerService {
    private readonly baseUrl = `${environment.restApiUrl}fleet-trailer-endpoint`;

    constructor(private http: HttpClient) {}

    searchTrailerTypes(filters: FleetTrailerTypeSearchParams): Observable<APIListWrapper<FleetTrailerType>> {
        let params = new HttpParams()
            .set('owningCompanyId', filters.owningCompanyId.toString())
            .set('page', (filters.page ?? 0).toString())
            .set('pageSize', (filters.pageSize ?? 20).toString());

        params = this.appendIfPresent(params, 'active', filters.active);
        params = this.appendIfPresent(params, 'keyword', filters.keyword);

        return this.http.get<APIListWrapper<FleetTrailerType>>(`${this.baseUrl}/trailer-types`, { params });
    }

    getTrailerType(id: number): Observable<APIResponseWrapper<FleetTrailerType>> {
        return this.http.get<APIResponseWrapper<FleetTrailerType>>(`${this.baseUrl}/trailer-types/${id}`);
    }

    createTrailerType(request: FleetTrailerType): Observable<APIResponseWrapper<FleetTrailerType>> {
        return this.http.post<APIResponseWrapper<FleetTrailerType>>(`${this.baseUrl}/trailer-types`, request);
    }

    updateTrailerType(id: number, request: FleetTrailerType): Observable<APIResponseWrapper<FleetTrailerType>> {
        return this.http.put<APIResponseWrapper<FleetTrailerType>>(`${this.baseUrl}/trailer-types/${id}`, request);
    }

    updateTrailerTypeActive(id: number, active: boolean): Observable<APIResponseWrapper<FleetTrailerType>> {
        const params = new HttpParams().set('active', active.toString());
        return this.http.patch<APIResponseWrapper<FleetTrailerType>>(`${this.baseUrl}/trailer-types/${id}/active`, null, { params });
    }

    searchTrailers(filters: FleetTrailerSearchParams): Observable<APIListWrapper<FleetTrailer>> {
        let params = new HttpParams()
            .set('owningCompanyId', filters.owningCompanyId.toString())
            .set('page', (filters.page ?? 0).toString())
            .set('pageSize', (filters.pageSize ?? 20).toString());

        params = this.appendIfPresent(params, 'active', filters.active);
        params = this.appendIfPresent(params, 'status', filters.status);
        params = this.appendIfPresent(params, 'trailerTypeId', filters.trailerTypeId);
        params = this.appendIfPresent(params, 'transportationAgencyId', filters.transportationAgencyId);
        params = this.appendIfPresent(params, 'keyword', filters.keyword);

        return this.http.get<APIListWrapper<FleetTrailer>>(`${this.baseUrl}/trailers`, { params });
    }

    getTrailer(id: number): Observable<APIResponseWrapper<FleetTrailer>> {
        return this.http.get<APIResponseWrapper<FleetTrailer>>(`${this.baseUrl}/trailers/${id}`);
    }

    createTrailer(request: FleetTrailer): Observable<APIResponseWrapper<FleetTrailer>> {
        return this.http.post<APIResponseWrapper<FleetTrailer>>(`${this.baseUrl}/trailers`, this.normalizeTrailerDates(request));
    }

    updateTrailer(id: number, request: FleetTrailer): Observable<APIResponseWrapper<FleetTrailer>> {
        return this.http.put<APIResponseWrapper<FleetTrailer>>(`${this.baseUrl}/trailers/${id}`, this.normalizeTrailerDates(request));
    }

    updateTrailerStatus(id: number, status: string): Observable<APIResponseWrapper<FleetTrailer>> {
        const params = new HttpParams().set('status', status);
        return this.http.patch<APIResponseWrapper<FleetTrailer>>(`${this.baseUrl}/trailers/${id}/status`, null, { params });
    }

    updateTrailerActive(id: number, active: boolean): Observable<APIResponseWrapper<FleetTrailer>> {
        const params = new HttpParams().set('active', active.toString());
        return this.http.patch<APIResponseWrapper<FleetTrailer>>(`${this.baseUrl}/trailers/${id}/active`, null, { params });
    }

    private appendIfPresent(params: HttpParams, key: string, value: unknown): HttpParams {
        if (value === null || value === undefined || value === '') {
            return params;
        }
        return params.set(key, value.toString());
    }

    private normalizeTrailerDates(request: FleetTrailer): FleetTrailer {
        return {
            ...request,
            registrationExpiry: this.toDateParam(request.registrationExpiry),
            insuranceExpiry: this.toDateParam(request.insuranceExpiry)
        };
    }

    private toDateParam(value?: string | Date): string | undefined {
        if (!value) {
            return undefined;
        }
        if (value instanceof Date) {
            return value.toISOString().slice(0, 10);
        }
        return value.toString().slice(0, 10);
    }
}
